import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  MessageSquare, 
  Send, 
  Reply, 
  Search, 
  Filter,
  MoreHorizontal,
  Star,
  Archive,
  Trash2,
  User,
  Clock,
  CheckCircle,
  XCircle
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface InMailManagerProps {
  isConnected: boolean;
}

interface InMailThread {
  id: string;
  recipient: {
    name: string;
    title: string;
    company: string;
    avatar?: string;
  };
  subject: string;
  lastMessage: string;
  status: 'sent' | 'replied' | 'read' | 'unread';
  timestamp: Date;
  messageCount: number;
  isStarred?: boolean;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: 'recruiting' | 'sales' | 'networking';
  responseRate: number;
}

export const InMailManager = ({ isConnected }: InMailManagerProps) => {
  const [activeTab, setActiveTab] = useState("inbox");
  const [showCompose, setShowCompose] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  // Mock data
  const [inMailThreads, setInMailThreads] = useState<InMailThread[]>([
    {
      id: '1',
      recipient: {
        name: 'Sarah Johnson',
        title: 'Senior Software Engineer',
        company: 'Tech Innovations Inc',
        avatar: '/api/placeholder/40/40'
      },
      subject: 'Exciting opportunity at our company',
      lastMessage: 'Thanks for reaching out! I\'d love to learn more about this role.',
      status: 'replied',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      messageCount: 3,
      isStarred: true
    },
    {
      id: '2',
      recipient: {
        name: 'Michael Chen',
        title: 'Product Manager',
        company: 'Growth Solutions',
        avatar: '/api/placeholder/40/40'
      },
      subject: 'Partnership opportunity discussion',
      lastMessage: 'I appreciate your interest in collaborating. Let me discuss this with my team.',
      status: 'read',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      messageCount: 2
    },
    {
      id: '3',
      recipient: {
        name: 'Emily Rodriguez',
        title: 'Marketing Director',
        company: 'Digital Marketing Pro',
        avatar: '/api/placeholder/40/40'
      },
      subject: 'Networking and industry insights',
      lastMessage: 'Hi Emily, I came across your profile and was impressed by your work in digital marketing...',
      status: 'sent',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      messageCount: 1
    }
  ]);

  const templates: Template[] = [
    {
      id: '1',
      name: 'Software Engineer Outreach',
      subject: 'Exciting Software Engineering Opportunity at {company}',
      content: `Hi {name},

I came across your profile and was impressed by your experience in {technology}. We have an exciting opportunity for a Senior Software Engineer at our company that I think would be a perfect fit for your skills.

The role offers:
- Competitive salary and equity
- Flexible remote work options
- Cutting-edge technology stack
- Collaborative team environment

Would you be open to a brief conversation to learn more?

Best regards,
{sender_name}`,
      category: 'recruiting',
      responseRate: 34.2
    },
    {
      id: '2',
      name: 'Partnership Proposal',
      subject: 'Strategic Partnership Opportunity',
      content: `Hello {name},

I hope this message finds you well. I've been following {company}'s impressive growth and believe there's a valuable opportunity for our companies to collaborate.

At {sender_company}, we specialize in {service}, and I see potential synergies with your work at {company}.

Would you be interested in exploring a partnership? I'd love to schedule a brief call to discuss how we might work together.

Looking forward to your response.

Best,
{sender_name}`,
      category: 'sales',
      responseRate: 28.7
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'replied':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Replied</Badge>;
      case 'read':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Read</Badge>;
      case 'sent':
        return <Badge variant="secondary">Sent</Badge>;
      case 'unread':
        return <Badge variant="outline">Unread</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'replied':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'read':
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'sent':
        return <Clock className="h-4 w-4 text-orange-600" />;
      case 'unread':
        return <XCircle className="h-4 w-4 text-gray-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <MessageSquare className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">Connect LinkedIn First</h3>
              <p className="text-muted-foreground">Connect your LinkedIn account to manage InMail</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">InMail Manager</h2>
          <p className="text-muted-foreground">Send and manage your LinkedIn messages</p>
        </div>
        <Dialog open={showCompose} onOpenChange={setShowCompose}>
          <DialogTrigger asChild>
            <Button>
              <Send className="h-4 w-4 mr-2" />
              Compose InMail
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Compose New InMail</DialogTitle>
              <DialogDescription>
                Send a personalized message to a LinkedIn connection
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Recipient</Label>
                  <Input placeholder="Search for LinkedIn profile..." />
                </div>
                <div className="space-y-2">
                  <Label>Template</Label>
                  <select className="w-full p-2 border rounded-md">
                    <option value="">Choose template...</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.responseRate}% response rate)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input placeholder="Subject line..." />
              </div>
              
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea 
                  placeholder="Write your personalized message..."
                  className="min-h-40"
                />
                <div className="text-sm text-muted-foreground">
                  Tip: Personalized messages get 3x higher response rates
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCompose(false)}>
                  Cancel
                </Button>
                <Button variant="outline">
                  Save Draft
                </Button>
                <Button>
                  <Send className="h-4 w-4 mr-2" />
                  Send InMail
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search messages..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>

          {/* Message List */}
          <div className="space-y-2">
            {inMailThreads.map((thread) => (
              <Card key={thread.id} className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={thread.recipient.avatar} alt={thread.recipient.name} />
                      <AvatarFallback>
                        {thread.recipient.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium truncate">{thread.recipient.name}</h4>
                          {thread.isStarred && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {getStatusIcon(thread.status)}
                          {format(thread.timestamp, 'MMM d, h:mm a')}
                        </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-1">
                        {thread.recipient.title} at {thread.recipient.company}
                      </p>
                      
                      <p className="font-medium text-sm mb-2 truncate">{thread.subject}</p>
                      
                      <p className="text-sm text-muted-foreground truncate mb-2">
                        {thread.lastMessage}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(thread.status)}
                          {thread.messageCount > 1 && (
                            <Badge variant="outline" className="text-xs">
                              {thread.messageCount} messages
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm">
                            <Reply className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Archive className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sent">
          <Card>
            <CardContent className="p-6 text-center">
              <Send className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">Your sent messages will appear here</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Message Templates</h3>
            <Button variant="outline">
              <MessageSquare className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
          
          <div className="grid gap-4">
            {templates.map((template) => (
              <Card key={template.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <CardDescription>{template.subject}</CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-600">{template.responseRate}%</p>
                      <p className="text-xs text-muted-foreground">Response Rate</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-3">{template.content}</p>
                  <div className="flex items-center justify-between mt-3">
                    <Badge variant="outline">{template.category}</Badge>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">Edit</Button>
                      <Button size="sm">Use Template</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <div className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">87%</p>
                    <p className="text-sm text-muted-foreground">Response Rate</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">156</p>
                    <p className="text-sm text-muted-foreground">Messages Sent</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold">2.3 days</p>
                    <p className="text-sm text-muted-foreground">Avg Response Time</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>InMail Performance</CardTitle>
                <CardDescription>Detailed analytics coming soon</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Analytics dashboard under development</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};